// @flow strict
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
// $FlowFixMe: unresolved
import styled from 'styled-components';
import type { StyledComponent } from 'styled-components';
import { isEqual } from 'lodash';

import type { ThemeInterface } from 'theme';
import connect from 'stores/connect';
import { Button, FormGroup, InputGroup, FormControl, HelpBlock } from 'components/graylog';
import ColorPickerPopover from 'components/common/ColorPickerPopover';
import Icon from 'components/common/Icon';
import StoreProvider from 'injection/StoreProvider';
import ObjectUtils from 'util/ObjectUtils';
import FormUtils from 'util/FormsUtils';
import { isPermitted } from 'util/PermissionsMixin';

const CurrentUserStore = StoreProvider.getStore('CurrentUser');

const Header = styled.h4`
  margin: 15px 0 3px;
`;

const ColorPickerPreview: StyledComponent<{color: string}, ThemeInterface, *> = styled.span(({ color }) => `
  background-color: ${color};
  width: 10px;
  height: 10px;
  display: inherit;
  margin-right: 5px;
  margin-top: 3px;
`);

const DEFAULT = {
  badge_enable: false,
  badge_color: '#689f38',
  badge_text: '',
};

type BadgeConfig = {
  badge_enable: boolean,
  badge_color: string,
  badge_text: string,
};

type Props = {
  config: BadgeConfig,
  updateConfig: (BadgeConfig) => void,
  currentUser: {
    permissions: [],
  },
};

const HeaderBadgeConfiguration = ({ config, updateConfig, currentUser }: Props) => {
  const [badgeConfig, setBadgeConfig] = useState(DEFAULT);
  const [hasChanged, setHasChanged] = useState(false);
  const [warning, setWarning] = useState({});

  useEffect(() => {
    if (config) {
      setBadgeConfig(config);
    }
  }, [config]);

  const _saveConfig = useCallback(() => {
    setHasChanged(false);
    updateConfig(badgeConfig);
  });

  const handleColorChange = useCallback((color, _, hidePopover) => {
    setHasChanged(!config || config.badge_color !== color);
    const newBadgeConfig = { ...badgeConfig, badge_color: color };
    setBadgeConfig(newBadgeConfig);

    hidePopover();
  });

  const validate = () => {
    if (badgeConfig.badge_text.length > 5) {
      warning.badge_text = 'Should be maximum 5 characters long';
    } else {
      warning.badge_text = null;
    }

    setWarning(warning);
  };

  const _onUpdate = useCallback((field) => {
    return (value) => {
      const update = ObjectUtils.clone(badgeConfig);

      if (typeof value === 'object') {
        update[field] = FormUtils.getValueFromInput(value.target);
      } else {
        update[field] = value;
      }

      setHasChanged(!isEqual(config, update));
      setBadgeConfig(update);
      validate();
    };
  });

  const isDisabled = !isPermitted(currentUser.permissions, 'clusterconfigentry:edit');

  return (
    <div>
      <Header>Header Badge</Header>
      <FormGroup validationState={warning.badge_text ? 'warning' : null}>
        <InputGroup>
          <InputGroup.Addon>
            <input type="checkbox"
                   id="badge-enable"
                   data-testid="badge-enable"
                   checked={badgeConfig.badge_enable}
                   onChange={_onUpdate('badge_enable')}
                   disabled={isDisabled} />
          </InputGroup.Addon>

          <FormControl type="text"
                       id="badge-text"
                       data-testid="badge-text"
                       label="Badge Text"
                       value={badgeConfig.badge_text}
                       onChange={_onUpdate('badge_text')}
                       help={warning.badge_text ? warning.badge_text : 'The text of the badge. Not more than five characters.'}
                       disabled={isDisabled} />

          <InputGroup.Button>
            <ColorPickerPopover id="badge-color"
                                placement="left"
                                color={badgeConfig.badge_color}
                                triggerNode={(
                                  <Button disabled={isDisabled}>
                                    <ColorPickerPreview color={badgeConfig.badge_color} />
                                    <Icon name="paint-brush" /> Color
                                  </Button>
                                )}
                                onChange={handleColorChange} />
          </InputGroup.Button>
        </InputGroup>

        <HelpBlock>{warning.badge_text ? warning.badge_text : 'The text of the badge. Not more than five characters recommended.'}</HelpBlock>

        {!isDisabled && <Button bsSize="xsmall" bsStyle="info" onClick={_saveConfig} disabled={!hasChanged}>Update Badge</Button>}
      </FormGroup>
    </div>
  );
};

HeaderBadgeConfiguration.propTypes = {
  config: PropTypes.shape({
    badge_text: PropTypes.string,
    badge_color: PropTypes.string,
    badge_enable: PropTypes.bool,
  }),
  currentUser: PropTypes.shape({
    permissions: PropTypes.array,
  }),
  warning: PropTypes.shape({
    badge_text: PropTypes.string,
  }),
  updateConfig: PropTypes.func,
};

HeaderBadgeConfiguration.defaultProps = {
  updateConfig: () => {},
  currentUser: {},
  config: {
    badge_text: '',
    badge_color: '#689f38',
    badge_enable: false,
  },
  warning: {
    badge_text: null,
  },
};

export default connect(HeaderBadgeConfiguration,
  { currentUser: CurrentUserStore },
  ({ currentUser, ...otherProps }) => ({
    currentUser: currentUser ? currentUser.currentUser : currentUser,
    ...otherProps,
  }));
